import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Badge,
} from '@startupquickstart/frontend';
import { Plus, MinusCircle } from 'lucide-react';

export interface FuzzyMatchSuggestion {
  id: string;
  name: string;
  score: number;
}

export interface PendingFuzzyMatch {
  field: string;
  inputValue: string;
  rowNumbers: number[];
  suggestions: FuzzyMatchSuggestion[];
}

// Special values for non-ID selections
export const FUZZY_MATCH_CREATE_NEW = '__CREATE_NEW__';
export const FUZZY_MATCH_LEAVE_BLANK = '__LEAVE_BLANK__';

interface FuzzyMatchConfirmProps {
  matches: PendingFuzzyMatch[];
  selections: Record<string, string | null>;
  onSelectionChange: (key: string, value: string | null) => void;
}

const fieldLabels: Record<string, string> = {
  greaterArea: 'Greater Area',
  neighborhood: 'Neighborhood',
  tourType: 'Tour Type',
  attractionType: 'Attraction Type',
};

export function FuzzyMatchConfirm({
  matches,
  selections,
  onSelectionChange,
}: FuzzyMatchConfirmProps) {
  console.log('FuzzyMatchConfirm rendering with:', { matches, selections });

  if (matches.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
        {matches.map((match) => {
          const selectionKey = `${match.field}:${match.inputValue}`;
          const currentSelection = selections[selectionKey];

          return (
            <Card key={selectionKey}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    {fieldLabels[match.field] || match.field}:{' '}
                    <span className="text-primary">"{match.inputValue}"</span>
                  </CardTitle>
                  <Badge variant="secondary">
                    Affects {match.rowNumbers.length}{' '}
                    {match.rowNumbers.length === 1 ? 'row' : 'rows'}
                  </Badge>
                </div>
                <CardDescription>
                  Rows: {match.rowNumbers.slice(0, 10).join(', ')}
                  {match.rowNumbers.length > 10 && ` and ${match.rowNumbers.length - 10} more`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={currentSelection ?? ''}
                  onValueChange={(value: string) => onSelectionChange(selectionKey, value || null)}
                >
                  {match.suggestions && match.suggestions.length > 0 && (
                    <>
                      {match.suggestions.map((suggestion) => (
                        <div
                          key={suggestion.id}
                          className="flex items-center space-x-3 space-y-0 py-2"
                        >
                          <RadioGroupItem value={suggestion.id} id={`${selectionKey}-${suggestion.id}`} />
                          <Label
                            htmlFor={`${selectionKey}-${suggestion.id}`}
                            className="flex items-center gap-2 font-normal cursor-pointer"
                          >
                            <span>{suggestion.name}</span>
                            <Badge
                              variant={suggestion.score >= 80 ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {suggestion.score}% match
                            </Badge>
                          </Label>
                        </div>
                      ))}
                      <div className="border-t mt-2 pt-3" />
                    </>
                  )}

                  {/* Create new option */}
                  <div className="flex items-center space-x-3 space-y-0 py-2">
                    <RadioGroupItem value={FUZZY_MATCH_CREATE_NEW} id={`${selectionKey}-create`} />
                    <Label
                      htmlFor={`${selectionKey}-create`}
                      className="flex items-center gap-2 font-normal cursor-pointer text-emerald-600"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Create "{match.inputValue}"</span>
                    </Label>
                  </div>

                  {/* Leave blank option */}
                  <div className="flex items-center space-x-3 space-y-0 py-2">
                    <RadioGroupItem value={FUZZY_MATCH_LEAVE_BLANK} id={`${selectionKey}-blank`} />
                    <Label
                      htmlFor={`${selectionKey}-blank`}
                      className="flex items-center gap-2 font-normal cursor-pointer text-muted-foreground"
                    >
                      <MinusCircle className="h-4 w-4" />
                      <span>Leave blank (import without {fieldLabels[match.field]?.toLowerCase() || match.field})</span>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
}
